import { createContext, useContext, useState, useEffect } from 'react'
import axios from 'axios'
import { API_BASE_URL, AUTH_ENDPOINTS } from '../constants/apis'

var userEmail = ""

const AuthContext = createContext()

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Check for existing authentication
    const accessToken = localStorage.getItem('accessToken')

    if (accessToken) {
      try {
        setIsAuthenticated(true)
      } catch (error) {
        console.error('Error parsing user data:', error)
      }
    }

    setIsLoading(false)
  }, [])

  const login = async (email, password) => {
    try {
      setIsLoading(true)
      userEmail = String(email)

      const bodyData = {
        email: String(email),
        password: String(password)
      }

      const response = await axios.post(`${API_BASE_URL}${AUTH_ENDPOINTS.LOGIN}`, bodyData, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      const { access_token, refresh_token } = response.data
      localStorage.setItem('accessToken', access_token)
      localStorage.setItem('refreshToken', refresh_token)

      setIsAuthenticated(true)

      return { success: true }
    } catch (error) {
      console.error('Login error:', error)
      return { success: false, error: error.response?.data?.message || 'Login failed' }
    } finally {
      setIsLoading(false)
    }
  }

  const logout = async () => {
    localStorage.clear();
  }

  const register = async (email, password) => {
    try {
      setIsLoading(true)
      userEmail = String(email)

      const bodyData = {
        email: String(email),
        password: String(password)
      }

      const response = await axios.post(`${API_BASE_URL}${AUTH_ENDPOINTS.REGISTER}`, bodyData, {
        headers: {
          "Content-Type": "application/json",
        },
      })

      const { access_token, refresh_token } = response.data

      setIsAuthenticated(true)

      return { success: true }
    } catch (error) {
      console.error('Registration error:', error)
      return { success: false, error: error.response?.data?.message || 'Registration failed' }
    } finally {
      setIsLoading(false)
    }
  }

  const updateUser = async (updatedData) => {
    try {
      const accessToken = localStorage.getItem('accessToken')
      const response = await axios.put(`${API_BASE_URL}${AUTH_ENDPOINTS.PROFILE}`, updatedData, {
        headers: {
          "Authorization": `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      })

      const updatedUser = response.data.user

      return { success: true, user: updatedUser }
    } catch (error) {
      console.error('Update user error:', error)
      return { success: false, error: error.response?.data?.message || 'Failed to update profile' }
    }
  }

  const refreshToken = async () => {
    try {
      const refreshTokenValue = localStorage.getItem('refreshToken')
      if (!refreshTokenValue) {
        throw new Error('No refresh token available')
      }

      const response = await axios.post(`${API_BASE_URL}${AUTH_ENDPOINTS.REFRESH}`, {}, {
        headers: {
          'X-Refresh-Token': refreshTokenValue
        }
      })

      const { access_token, refresh_token: newRefreshToken } = response.data
      localStorage.setItem('accessToken', access_token)
      if (newRefreshToken) {
        localStorage.setItem('refreshToken', newRefreshToken)
      }

      return { success: true, access_token }
    } catch (error) {
      console.error('Token refresh error:', error)
      return { success: false, error: 'Token refresh failed' }
    }
  }

  const changePassword = async (currentPassword, newPassword) => {
    try {
      setIsLoading(true)
      const accessToken = localStorage.getItem('accessToken')

      const bodyData = {
        currentPassword: String(currentPassword),
        newPassword: String(newPassword)
      }

      const response = await axios.post(`${API_BASE_URL}${AUTH_ENDPOINTS.CHANGE_PASSWORD}`, bodyData, {
        headers: {
          "Authorization": `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      })

      return { success: true, message: 'Password changed successfully' }
    } catch (error) {
      console.error('Change password error:', error)
      return { success: false, error: error.response?.data?.message || 'Failed to change password' }
    } finally {
      setIsLoading(false)
    }
  }

  const value = {
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
    updateUser,
    refreshToken,
    changePassword
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
